import { useTranslation } from '@bcba/react-hooks';
import { ExplorerClientDigitalDossierStatusSerializer } from '@bcba/serializers';
import {
  CLIENT_DIGITAL_DOSSIER_SECTIONS_STATUS,
  VALIDATION_STATUSES,
} from '@bcba/types';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { getDigitalDossierStatus } from '../../../services/digital-dossier-status';
import { BankingInfo } from '../DigitalDossierSection/BankingInfo';
import { DocumentationAndForms } from '../DigitalDossierSection/DocumentationAndForms';
import { GeneralInfo } from '../DigitalDossierSection/GeneralInfo';
import { LegalRepresentatives } from '../DigitalDossierSection/LegalRepresentatives';
import { TaxData } from '../DigitalDossierSection/TaxInfo/TaxInfo';
import { AsideTabData } from '../shared/DigitalDossierAside';

const mapDigitalDossierStatus = {
  [CLIENT_DIGITAL_DOSSIER_SECTIONS_STATUS.APPROVED]:
    VALIDATION_STATUSES.APPROVED,
  [CLIENT_DIGITAL_DOSSIER_SECTIONS_STATUS.PENDING_APPROVAL]:
    VALIDATION_STATUSES.PENDING_APPROVAL,
  [CLIENT_DIGITAL_DOSSIER_SECTIONS_STATUS.PENDING_DATA_UPLOAD]:
    VALIDATION_STATUSES.INCOMPLETE,
  [CLIENT_DIGITAL_DOSSIER_SECTIONS_STATUS.PENDING_REVIEW]:
    VALIDATION_STATUSES.PENDING_CHANGE_REQUEST,
  [VALIDATION_STATUSES.INCOMPLETE]: VALIDATION_STATUSES.INCOMPLETE,
};

type DigitalDossierSectionContextType = {
  tabsData: AsideTabData[];
  digitalDossierStatus?: ExplorerClientDigitalDossierStatusSerializer;
  refetchDigitalDossierStatus: (
    options?: RefetchOptions | undefined,
  ) => Promise<
    QueryObserverResult<ExplorerClientDigitalDossierStatusSerializer, Error>
  >;
};

const DigitalDossierSectionContext = createContext<
  DigitalDossierSectionContextType | undefined
>(undefined);
type DigitalDossierSectionProviderProps = {
  children: ReactNode;
};

export const DigitalDossierSectionProvider: React.FC<
  DigitalDossierSectionProviderProps
> = ({ children }) => {
  const { t } = useTranslation();

  const {
    explorerDigitalDossierEnableBankingInfo,
    explorerDigitalDossierEnableTaxInfo,
    explorerDigitalDossierEnableRepresentantsInfo,
    explorerDigitalDossierEnableFormsAndDocsInfo,
  } = useFlags();

  const { data: digitalDossierStatus, refetch: refetchDigitalDossierStatus } =
    useQuery({
      queryKey: ['digital-dossier-status'],
      queryFn: getDigitalDossierStatus,
    });

  const tabsData = useMemo(() => {
    const tabs = [
      {
        title: t('common.companyGeneralInformation'),
        content: <GeneralInfo />,
        status:
          mapDigitalDossierStatus[
            digitalDossierStatus?.sections.generalInfo ??
              VALIDATION_STATUSES.INCOMPLETE
          ],
      },
    ];

    if (explorerDigitalDossierEnableBankingInfo) {
      tabs.push({
        title: t('common.bankData'),
        content: <BankingInfo />,
        status:
          mapDigitalDossierStatus[
            digitalDossierStatus?.sections.bankingInfo ??
              VALIDATION_STATUSES.INCOMPLETE
          ],
      });
    }

    if (explorerDigitalDossierEnableTaxInfo) {
      tabs.push({
        title: t('common.taxData'),
        content: <TaxData />,
        status:
          mapDigitalDossierStatus[
            digitalDossierStatus?.sections.taxInfo ??
              VALIDATION_STATUSES.INCOMPLETE
          ],
      });
    }

    if (explorerDigitalDossierEnableRepresentantsInfo) {
      tabs.push({
        title: t('common.legalRepresentatives'),
        content: <LegalRepresentatives />,
        status:
          mapDigitalDossierStatus[
            digitalDossierStatus?.sections.representantsInfo ??
              VALIDATION_STATUSES.INCOMPLETE
          ],
      });
    }

    if (explorerDigitalDossierEnableFormsAndDocsInfo) {
      tabs.push({
        title: t('common.documentationAndForms'),
        content: <DocumentationAndForms />,
        status:
          mapDigitalDossierStatus[
            digitalDossierStatus?.sections.formsAndDocsInfo ??
              VALIDATION_STATUSES.INCOMPLETE
          ],
      });
    }

    return tabs;
  }, [
    t,
    digitalDossierStatus,
    explorerDigitalDossierEnableBankingInfo,
    explorerDigitalDossierEnableTaxInfo,
    explorerDigitalDossierEnableRepresentantsInfo,
    explorerDigitalDossierEnableFormsAndDocsInfo,
  ]);

  return (
    <DigitalDossierSectionContext.Provider
      value={{
        tabsData,
        digitalDossierStatus,
        refetchDigitalDossierStatus,
      }}
    >
      {children}
    </DigitalDossierSectionContext.Provider>
  );
};

export const useDigitalDossierSectionContext =
  (): DigitalDossierSectionContextType => {
    const context = useContext(DigitalDossierSectionContext);
    if (context === undefined) {
      throw new Error(
        'useDigitalDossierSection must be used within a DigitalDossierSectionProvider',
      );
    }
    return context;
  };
